import { Link, useNavigate } from 'react-router-dom';
import { Home, Search, Heart, User, Menu, X, LogOut, Shield, ClipboardList, Map, BarChart3 } from 'lucide-react';
import { useState, useCallback } from 'react';
import { Button } from '@/components/ui/button';
import { useAuth } from '@/contexts/AuthContext';
import { useAdminRole } from '@/hooks/useAdminRole';
import { CitySwitcher } from '@/components/CitySwitcher';
import { PushNotificationToggle } from '@/components/PushNotificationToggle';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';

// Prefetch the Map page module on hover for faster navigation
const prefetchMap = () => {
  import('@/pages/Map');
};

export function Header() {
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const { user, signOut } = useAuth();
  const { isAdmin } = useAdminRole();
  const navigate = useNavigate();

  const handleSignOut = async () => {
    await signOut();
    navigate('/');
  };

  return (
    <header className="sticky top-0 z-50 bg-card/95 backdrop-blur-md border-b" style={{ minHeight: '64px', contain: 'layout style' }}>
      <div className="container">
        <div className="flex items-center justify-between h-16">
          {/* Logo */}
          <div className="flex items-center gap-4">
            <Link to="/" className="flex items-center gap-2" onClick={() => window.scrollTo({ top: 0, behavior: 'smooth' })}>
              <div className="w-9 h-9 rounded-lg bg-primary flex items-center justify-center">
                <Home className="w-5 h-5 text-primary-foreground" />
              </div>
              <span className="font-display text-xl font-semibold tracking-tight hidden sm:inline">
                Book A Viewing
              </span>
            </Link>
            <CitySwitcher />
          </div>

          {/* Desktop Navigation */}
          <nav className="hidden md:flex items-center gap-1">
            <Button variant="ghost" className="gap-2" onClick={() => {
              navigate('/search', { state: { reset: Date.now() } });
              window.scrollTo({ top: 0, behavior: 'smooth' });
            }}>
              <Search className="w-4 h-4" />
              Search
            </Button>
            <Link to="/map" onMouseEnter={prefetchMap} onFocus={prefetchMap}>
              <Button variant="ghost">Map View</Button>
            </Link>
            <Link to="/viewing-plan">
              <Button variant="ghost" className="gap-2">
                <ClipboardList className="w-4 h-4" />
                Viewing Plan
              </Button>
            </Link>
            <Button variant="ghost" className="gap-2" onClick={() => {
              navigate('/market-stats', { state: { reset: Date.now() } });
              window.scrollTo({ top: 0, behavior: 'smooth' });
            }}>
              <BarChart3 className="w-4 h-4" />
              Market Stats
            </Button>
            <Button variant="ghost" onClick={() => {
              navigate('/news', { state: { reset: Date.now() } });
              window.scrollTo({ top: 0, behavior: 'smooth' });
            }}>
              News
            </Button>
          </nav>

          {/* Desktop Actions */}
          <div className="hidden md:flex items-center gap-2">
            {user ? (
              <>
                <PushNotificationToggle />
                <Link to="/dashboard/favorites">
                  <Button variant="ghost" size="icon" aria-label="Saved favorites">
                    <Heart className="w-5 h-5" />
                  </Button>
                </Link>
                <DropdownMenu>
                  <DropdownMenuTrigger asChild>
                    <Button variant="outline" className="gap-2">
                      <User className="w-4 h-4" />
                      Account
                    </Button>
                  </DropdownMenuTrigger>
                  <DropdownMenuContent align="end" className="w-48">
                    <DropdownMenuItem onClick={() => navigate('/dashboard')}>
                      Dashboard
                    </DropdownMenuItem>
                    <DropdownMenuItem onClick={() => navigate('/dashboard/favorites')}>
                      Saved Properties
                    </DropdownMenuItem>
                    <DropdownMenuItem onClick={() => navigate('/dashboard/searches')}>
                      Saved Searches
                    </DropdownMenuItem>
                    <DropdownMenuItem onClick={() => navigate('/dashboard/history')}>
                      Recently Viewed
                    </DropdownMenuItem>
                    {isAdmin && (
                      <>
                        <DropdownMenuSeparator />
                        <DropdownMenuItem onClick={() => navigate('/admin/properties')}>
                          <Shield className="w-4 h-4 mr-2" />
                          Admin Panel
                        </DropdownMenuItem>
                      </>
                    )}
                    <DropdownMenuSeparator />
                    <DropdownMenuItem onClick={handleSignOut} className="text-destructive">
                      <LogOut className="w-4 h-4 mr-2" />
                      Sign Out
                    </DropdownMenuItem>
                  </DropdownMenuContent>
                </DropdownMenu>
              </>
            ) : (
              <>
                <Button variant="ghost" onClick={() => navigate('/auth')}>
                  Sign In
                </Button>
                <Button onClick={() => navigate('/auth?mode=signup')}>
                  Get Started
                </Button>
              </>
            )}
          </div>

          {/* Mobile Menu Button */}
          <Button
            variant="ghost"
            size="icon"
            className="md:hidden"
            onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
            aria-label={mobileMenuOpen ? 'Close menu' : 'Open menu'}
            aria-expanded={mobileMenuOpen}
          >
            {mobileMenuOpen ? <X className="w-5 h-5" /> : <Menu className="w-5 h-5" />}
          </Button>
        </div>

        {/* Mobile Menu */}
        {mobileMenuOpen && (
          <div className="md:hidden absolute left-0 right-0 top-full bg-card border-b shadow-lg z-50 py-4 animate-fade-in">
            <div className="px-2 pb-3 border-b mb-3">
              <CitySwitcher />
            </div>
            <nav className="flex flex-col gap-2">
              <Button variant="ghost" className="w-full justify-start gap-2" onClick={() => {
                setMobileMenuOpen(false);
                navigate('/search', { state: { reset: Date.now() } });
                window.scrollTo({ top: 0, behavior: 'smooth' });
              }}>
                <Search className="w-4 h-4" />
                Search Properties
              </Button>
              <Link to="/map" onClick={() => setMobileMenuOpen(false)} onTouchStart={prefetchMap}>
                <Button variant="ghost" className="w-full justify-start">
                  Map View
                </Button>
              </Link>
              <Link to="/viewing-plan" onClick={() => setMobileMenuOpen(false)}>
                <Button variant="ghost" className="w-full justify-start gap-2">
                  <ClipboardList className="w-4 h-4" />
                  Viewing Plan
                </Button>
              </Link>
              <Button variant="ghost" className="w-full justify-start gap-2" onClick={() => {
                setMobileMenuOpen(false);
                navigate('/market-stats', { state: { reset: Date.now() } });
                window.scrollTo({ top: 0, behavior: 'smooth' });
              }}>
                <BarChart3 className="w-4 h-4" />
                Market Stats
              </Button>
              <Button variant="ghost" className="w-full justify-start" onClick={() => {
                setMobileMenuOpen(false);
                navigate('/news', { state: { reset: Date.now() } });
                window.scrollTo({ top: 0, behavior: 'smooth' });
              }}>
                News
              </Button>
              {user ? (
                <>
                  <Link to="/dashboard/favorites" onClick={() => setMobileMenuOpen(false)}>
                    <Button variant="ghost" className="w-full justify-start gap-2">
                      <Heart className="w-4 h-4" />
                      Saved Properties
                    </Button>
                  </Link>
                  <Link to="/dashboard" onClick={() => setMobileMenuOpen(false)}>
                    <Button variant="ghost" className="w-full justify-start gap-2">
                      <User className="w-4 h-4" />
                      Dashboard
                    </Button>
                  </Link>
                  {isAdmin && (
                    <Link to="/admin/properties" onClick={() => setMobileMenuOpen(false)}>
                      <Button variant="ghost" className="w-full justify-start gap-2">
                        <Shield className="w-4 h-4" />
                        Admin Panel
                      </Button>
                    </Link>
                  )}
                  <Button
                    variant="ghost"
                    className="w-full justify-start gap-2 text-destructive"
                    onClick={() => {
                      handleSignOut();
                      setMobileMenuOpen(false);
                    }}
                  >
                    <LogOut className="w-4 h-4" />
                    Sign Out
                  </Button>
                </>
              ) : (
                <>
                  <Button 
                    variant="ghost" 
                    className="w-full justify-start"
                    onClick={() => {
                      navigate('/auth');
                      setMobileMenuOpen(false);
                    }}
                  >
                    Sign In
                  </Button>
                  <Button 
                    className="w-full"
                    onClick={() => {
                      navigate('/auth?mode=signup');
                      setMobileMenuOpen(false);
                    }}
                  >
                    Get Started
                  </Button>
                </>
              )}
            </nav>
          </div>
        )}
      </div>
    </header>
  );
}